import './ClientTargetPicker.scss';
import { ReactNode, useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdAdd, MdOutlineDelete } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { useAppSelector } from '../../../redux';
import * as UserRedux from '../../../redux/user';
import { GENERIC_CID, MappingTabConfig } from '../client-data-mapping-constants';

interface UseClientTargetPickerArgs {
  tab: MappingTabConfig;
  // Set when the caller is scoped to a single client (Client-scope AddMappingModal). Renders
  // a single locked slot instead of a picker; the "Mark as generic" affordance is suppressed
  // (a client-scoped page can't mark a domain generic -- that's a Global-Admin decision).
  fixedCid?: string;
  // Cids the picker should not offer as options -- used by CompanyPickerModal in a reassign
  // flow to exclude the current fromCid from the dropdown (nothing changes if a user "picks"
  // the same cid they're moving from).
  excludeCids?: string[];
  // DOM id on the mark-generic checkbox. Kept as a prop so multiple pickers on the same page
  // (should that ever happen) don't collide on `for=` label targets.
  checkboxId?: string;
  // On allowGeneric tabs, whether to visually group the two mutually-exclusive paths inside
  // an outlined box with an "OR" divider. Defaults to true (AddMappingModal). CompanyPickerModal
  // sets false so the assign/reassign flow reads as a simpler picker + trailing checkbox
  // stack -- the OR grouping is redundant there since the modal is scoped to one action.
  showOrWrapper?: boolean;
  // Suppress the "Mark as generic" checkbox even on allowGeneric tabs. Used by AddClientsModal
  // where the action is purely additive -- marking generic would nuke every existing client
  // assignment, which is the opposite of "add more clients" and would confuse the operator.
  hideMarkGeneric?: boolean;
}

interface ClientTargetPickerState {
  // The cids the caller should submit to. `markGeneric` collapses this to [GENERIC_CID];
  // otherwise it's the array of every non-null client slot. Empty means "nothing to submit"
  // (isValid will be false in that case).
  targetCids: string[];
  isValid: boolean;
  markGeneric: boolean;
  render: () => ReactNode;
}

// Shared picker for "which clients (or generic) should this item be assigned to?" Used by
// AddMappingModal (during a new-item add) and CompanyPickerModal (during an
// assign-from-unassigned or reassign flow). Owns its own state (cids + markGeneric) and
// exposes a render node so each modal composes its own outer chrome (title, submit button
// label, etc.).
//
// Multi-slot: "Add another client" appends a null slot; each slot renders its own dropdown
// that excludes cids picked in other slots so a client can't appear twice. On allowGeneric tabs
// (slack_channel, email_domain) the "Mark as generic" checkbox appears inside an outlined
// OR-wrapper alongside the picker -- the two paths are mutually exclusive so surfacing them as
// an either/or is clearer than two loose form groups stacked vertically.
export const useClientTargetPicker = ({
  tab,
  fixedCid,
  excludeCids = [],
  checkboxId = 'client-target-picker-mark-generic',
  showOrWrapper = true,
  hideMarkGeneric = false
}: UseClientTargetPickerArgs): ClientTargetPickerState => {
  const companies = useAppSelector(UserRedux.companiesSelector);
  const [cids, setCids] = useState<(string | null)[]>([fixedCid ?? null]);
  const [markGeneric, setMarkGeneric] = useState(false);

  const validCids = cids.filter((c): c is string => Boolean(c));
  const targetCids = markGeneric ? [GENERIC_CID] : validCids;
  const isValid = targetCids.length > 0;

  const clientPickerNode = fixedCid ? null : (
    <div className='client-target-picker'>
      <Form.Label>{cids.length > 1 ? 'Select Clients' : 'Select a Client'}</Form.Label>
      {cids.map((slot, i) => {
        const otherPicked = cids.filter((c, j): c is string => j !== i && Boolean(c));
        const optionsForSlot = companies
          .map(c => c.company)
          .filter(c => !excludeCids.includes(c) && !otherPicked.includes(c));
        const updateSlot = (next: string | null) => {
          setCids(prev => prev.map((existing, j) => (j === i ? next : existing)));
        };
        const removeSlot = () => setCids(prev => prev.filter((_, j) => j !== i));
        return (
          <div key={i} className='client-target-picker-slot'>
            <Dropdown
              size={Size.LG}
              searchable
              disabled={markGeneric}
              options={optionsForSlot}
              value={slot}
              onChange={updateSlot}
              placeholder={markGeneric ? 'N/A' : 'Select a client'}
            />
            {cids.length > 1 && (
              <IconButton
                appearance={Appearance.EMPTY}
                disabled={markGeneric}
                tooltip={{ text: 'Remove this client' }}
                onClick={removeSlot}
              >
                <MdOutlineDelete />
              </IconButton>
            )}
          </div>
        );
      })}
      <Button
        size={Size.LG}
        appearance={Appearance.EMPTY}
        disabled={markGeneric}
        leadingElement={<MdAdd />}
        onClick={() => setCids(prev => [...prev, null])}
      >
        Add another client
      </Button>
    </div>
  );

  const genericCheckboxNode =
    tab.allowGeneric && !fixedCid && !hideMarkGeneric ? (
      <Checkbox
        id={checkboxId}
        // Mutually exclusive with picking any client -- disable once at least one client slot
        // has a real cid so the two paths can't visibly conflict.
        disabled={validCids.length > 0}
        label='Mark as generic'
        checked={markGeneric}
        onChange={e => setMarkGeneric(e.target.checked)}
      />
    ) : null;

  const render = () => {
    // OR wrapper only makes sense when both halves render -- if the mark-generic checkbox is
    // suppressed there's nothing to be "OR"-ed against the client picker.
    if (tab.allowGeneric && !fixedCid && showOrWrapper && !hideMarkGeneric) {
      return (
        <div className='client-target-picker-or-wrapper'>
          {clientPickerNode}
          <div className='client-target-picker-or-divider'>OR</div>
          {genericCheckboxNode}
        </div>
      );
    }
    // No OR wrapper -- render whichever nodes are non-null stacked plainly.
    return (
      <>
        {clientPickerNode}
        {genericCheckboxNode}
      </>
    );
  };

  return { targetCids, isValid, markGeneric, render };
};
